import type { PropsWithChildren, ReactNode } from 'react';
import styled from 'styled-components';

import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { BackButton } from '@sorare/core/src/components/dialog/BackButton';
import { useSeriesNav } from '@sorare/core/src/hooks/commonGame/useSeriesNav';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop.ts';
import { desktopAndAbove } from '@sorare/core/src/style/mediaQuery.ts';
import { css } from '@sorare/css';

import { BoardWebAppHeader } from '#src/components/navigation/NewAppHeader/WebAppHeader/BoardWebAppHeader/index.tsx';

const Root = styled(Vertical)`
  padding: var(--unit) var(--double-unit) 0 var(--double-unit);

  @media ${desktopAndAbove} {
    padding: var(--unit) 0 0 var(--double-unit);
  }
`;

const titleClassName = css`
  height: calc(8 * var(--unit));
  overflow: visible;
  padding: var(--intermediate-unit) 0;
`;

type Props = {
  title?: ReactNode;
  onBack?: () => void;
  /* Rendered on the right of the title row, on every breakpoint — unlike
   * `children`, which move up into the user nav below laptop. */
  rightSlot?: ReactNode;
  className?: string;
};

export const SeriesBoardHeader = ({
  title,
  children,
  onBack,
  rightSlot,
  className,
}: PropsWithChildren<Props>) => {
  const seriesNav = useSeriesNav();

  const isLaptopAndAbove = useIsLaptop();
  const showUserNav = seriesNav && !isLaptopAndAbove;

  return (
    <Root className={className}>
      {!!showUserNav && <BoardWebAppHeader>{children}</BoardWebAppHeader>}
      <SBHorizontal>
        {(!!onBack || !!title) && (
          <Horizontal>
            {!!onBack && <BackButton onBack={onBack} color="quaternary" />}
            {!!title && <div className={titleClassName}>{title}</div>}
          </Horizontal>
        )}
        {!showUserNav && children}
        {rightSlot}
      </SBHorizontal>
    </Root>
  );
};
